import { useEffect, useState } from 'react';
import { ClipboardList, Crown, Hourglass, Link2, Users } from 'lucide-react';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { BookingPackDrawer } from './BookingPackDrawer';

interface PackStatus { attendees: number; sponsor: boolean; linkedDays: number }

const sameEmail = (a?: string | null, b?: string | null) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Opens the Session Hub pack for a booking, for its account manager or a
 * facilitator, once a facilitator has linked a session to the booking.
 * `linked` comes from the caller when known; otherwise the facilitator card
 * reads it from the pack status.
 */
export function BookingPackButton({ booking, compact = false, linked }: {
  booking: { bookingId: string; customer: string; startDate: string; endDate: string; accountTeamLeadEmail?: string; status: string };
  compact?: boolean;
  linked?: boolean;
}) {
  const { user, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<PackStatus | null>(null);
  const facilitator = can('Studio::ManageBookingAttendees');
  const allowed = facilitator || sameEmail(booking.accountTeamLeadEmail, user?.email);

  useEffect(() => {
    if (!facilitator || compact || open) return;
    api.get<{ status: Record<string, PackStatus> }>('/api/studio/bookings/pack-status', { params: { ids: booking.bookingId } })
      .then((r) => setStatus(r.data.status[booking.bookingId] ?? null))
      .catch(() => setStatus(null));
  }, [facilitator, compact, open, booking.bookingId]);

  if (!allowed || booking.status === 'cancelled' || booking.status === 'rejected') return null;

  const dates = booking.startDate === booking.endDate ? booking.startDate : `${booking.startDate} to ${booking.endDate}`;
  const isLinked = linked ?? (status ? status.linkedDays > 0 : undefined);

  if (compact && isLinked === false) {
    return (
      <span className="flex items-center gap-1 text-xs text-slate-400" title="The facilitator links their session to this booking from the project's Sessions page">
        <Hourglass className="w-3 h-3" /> Waiting for the facilitator
      </span>
    );
  }

  return (
    <>
      {compact ? (
        <button type="button" onClick={() => setOpen(true)} title="Session Hub pack" className="flex items-center gap-1 h-7 px-2.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-colors">
          <ClipboardList className="w-3 h-3" /> Pack
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#e2e8f0] px-4 py-3">
          <ClipboardList size={16} className="text-primary" />
          <div className="flex-1 min-w-[10rem]">
            <p className="text-sm font-semibold text-navy">Session Hub pack</p>
            <p className="text-xs text-slate-400 flex flex-wrap items-center gap-2">
              {isLinked === false ? "Not set up yet. Link it from the project's Sessions page (Link to booking)." : status ? (
                <>
                  <span className="flex items-center gap-1"><Users size={12} /> {status.attendees}</span>
                  <span className="flex items-center gap-1"><Crown size={12} /> {status.sponsor ? 'sponsor set' : 'no sponsor'}</span>
                  <span className="flex items-center gap-1"><Link2 size={12} /> {status.linkedDays} linked</span>
                </>
              ) : 'Attendees, agenda, access and retention'}
            </p>
          </div>
          {isLinked !== false && (
            <button type="button" onClick={() => setOpen(true)} className="flex items-center justify-center gap-2 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
              Open
            </button>
          )}
        </div>
      )}
      {open && <BookingPackDrawer bookingId={booking.bookingId} title={`${booking.customer} · ${dates}`} onClose={() => setOpen(false)} />}
    </>
  );
}
